import { useState } from "react";
import { Button } from "../lib/ui.js";
import { formatTime, formatWeekdayDayMonth } from "../lib/datetime.js";
import { BookingApiError, type BookingApi, type MyBooking } from "./api.js";
import { errorMessage, staffForServices, type PublicConfig } from "./helpers.js";
import { SlotCalendar, type PickedTime } from "./SlotCalendar.js";
import { VerifyStep } from "./VerifyStep.js";
import { DobCheck } from "./DobCheck.js";

type Stage =
  | { name: "verify"; note: string | null }
  | { name: "dob"; token: string }
  | { name: "list"; token: string; bookings: MyBooking[] }
  | { name: "cancel"; token: string; booking: MyBooking }
  | { name: "move"; token: string; booking: MyBooking }
  | { name: "done"; text: string };

/**
 * Cancel or move a booking. Every change needs its own fresh code: the code opens a session that can see the customer's bookings and is
 * used up by ONE cancellation or ONE move. To change a second booking the customer checks their mobile again.
 */
export function ManageFlow({ api, config, onBook }: { api: BookingApi; config: PublicConfig; onBook: () => void }) {
  const [stage, setStage] = useState<Stage>({ name: "verify", note: null });
  const [slot, setSlot] = useState<PickedTime | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  const restart = (note: string | null) => {
    setSlot(null);
    setError(null);
    setStage({ name: "verify", note });
  };

  /** A failed call: a dead session starts over, anything else is shown and the customer can try again. */
  function failed(err: unknown) {
    const code = err instanceof BookingApiError ? err.code : "unexpected_error";
    if (code === "invalid_session") restart(errorMessage(code));
    else setError(errorMessage(code, err instanceof BookingApiError ? err.detail : null));
  }

  async function onVerified(token: string) {
    setBusy(true);
    try {
      setStage({ name: "list", token, bookings: await api.myBookings(token) });
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  }

  async function doCancel(token: string, b: MyBooking) {
    setBusy(true);
    setError(null);
    try {
      await api.cancel(token, b.id);
      setStage({ name: "done", text: `Your booking on ${formatWeekdayDayMonth(b.starts_at)} at ${formatTime(b.starts_at)} has been cancelled.` });
    } catch (e) {
      failed(e);
    } finally {
      setBusy(false);
    }
  }

  async function doMove(token: string, b: MyBooking) {
    if (!slot) return;
    setBusy(true);
    setError(null);
    try {
      const r = await api.reschedule(token, b.id, slot.slot.starts_at, slot.resourceId);
      setStage({ name: "done", text: `Your booking has been moved to ${formatWeekdayDayMonth(r.starts_at)} at ${formatTime(r.starts_at)}.` });
    } catch (e) {
      const code = e instanceof BookingApiError ? e.code : "";
      failed(e);
      if (code === "slot_unavailable") {
        setSlot(null);
        setRefresh((n) => n + 1); // that time was just taken: look again
      }
    } finally {
      setBusy(false);
    }
  }

  const staffName = (id: string) => {
    const n = config.resources.find((r) => r.id === id)?.name;
    return n ? ` with ${n}` : "";
  };

  const summary = (b: MyBooking) => (
    <>
      <p style={{ margin: 0, fontWeight: 600 }}>{b.services.join(" + ")}</p>
      <p style={{ margin: "2px 0 0" }}>
        {formatWeekdayDayMonth(b.starts_at)} at {formatTime(b.starts_at)}
        {b.staff ? ` with ${b.staff}` : ""}
      </p>
      {b.guest_name && <p className="bk-muted" style={{ margin: "2px 0 0" }}>For {b.guest_name}</p>}
    </>
  );

  return (
    <section className={`bk-section${stage.name === "move" ? "" : " bk-narrow"}`} aria-live="polite">
      {stage.name === "verify" && (
        <VerifyStep
          api={api}
          purpose="manage"
          siteKey={config.turnstile_site_key}
          hasPhone={!!config.phone}
          message={stage.note}
          onVerified={(v) => (v.dobMode === "verify" ? setStage({ name: "dob", token: v.token }) : void onVerified(v.token))}
        />
      )}
      {stage.name === "dob" && (
        <div className="bk-card">
          <DobCheck api={api} token={stage.token} onOk={() => void onVerified(stage.token)} onEnded={(note) => restart(note)} />
        </div>
      )}
      {busy && stage.name === "verify" && <p className="bk-muted">Looking up your bookings...</p>}

      {stage.name === "list" && (
        <>
          <h2 className="bk-h">Your upcoming bookings</h2>
          {stage.bookings.length === 0 && (
            <div className="bk-card">
              <p style={{ margin: 0 }}>There are no upcoming bookings for this number.</p>
              <div style={{ marginTop: 12 }}><Button type="button" variant="primary" onClick={onBook}>Make a booking</Button></div>
            </div>
          )}
          <div className="bk-stack">
            {stage.bookings.map((b) => (
              <div key={b.id} className="bk-card">
                {summary(b)}
                {b.can_change ? (
                  <div className="bk-row" style={{ marginTop: 12 }}>
                    <Button type="button" onClick={() => { setSlot(null); setError(null); setStage({ name: "move", token: stage.token, booking: b }); }}>Change the time</Button>
                    <Button type="button" variant="danger" onClick={() => { setError(null); setStage({ name: "cancel", token: stage.token, booking: b }); }}>Cancel booking</Button>
                  </div>
                ) : (
                  <p className="bk-muted" style={{ margin: "10px 0 0" }}>It is too close to this booking to change it online. Please phone us.</p>
                )}
              </div>
            ))}
          </div>
          {stage.bookings.length > 0 && <p className="bk-muted" style={{ marginTop: 12 }}>Each change needs a new code, so we will ask you to check your mobile again for the next one.</p>}
          {error && <p className="bk-error" role="alert">{error}</p>}
        </>
      )}

      {stage.name === "cancel" && (
        <div className="bk-card">
          <h2 className="bk-h">Cancel this booking?</h2>
          {summary(stage.booking)}
          <div className="bk-row" style={{ marginTop: 14 }}>
            <Button type="button" variant="danger" disabled={busy} onClick={() => void doCancel(stage.token, stage.booking)}>{busy ? "Cancelling..." : "Yes, cancel it"}</Button>
            <Button type="button" variant="ghost" disabled={busy} onClick={() => api.myBookings(stage.token).then((bookings) => setStage({ name: "list", token: stage.token, bookings })).catch(failed)}>Keep it</Button>
          </div>
          {error && <p className="bk-error" role="alert">{error}</p>}
        </div>
      )}

      {stage.name === "move" && (
        <>
          <h2 className="bk-h">Choose a new time</h2>
          <div className="bk-card" style={{ marginBottom: 14 }}>
            <p className="bk-muted" style={{ margin: "0 0 4px" }}>Now</p>
            {summary(stage.booking)}
          </div>
          <SlotCalendar
            api={api}
            config={config}
            serviceIds={stage.booking.service_ids}
            durationMin={Math.round((Date.parse(stage.booking.ends_at) - Date.parse(stage.booking.starts_at)) / 60000)}
            columns={staffForServices(config.resources, stage.booking.service_ids)}
            selected={slot}
            onSelect={(p) => { setSlot(p); setError(null); }}
            refreshKey={refresh}
          />
          {slot && (
            <div className="bk-card" style={{ marginTop: 14 }}>
              <p style={{ margin: "0 0 12px" }}>Move to <strong>{formatWeekdayDayMonth(slot.slot.starts_at)} at {formatTime(slot.slot.starts_at)}</strong>{staffName(slot.resourceId)}?</p>
              <div className="bk-row">
                <Button type="button" variant="primary" disabled={busy} onClick={() => void doMove(stage.token, stage.booking)}>{busy ? "Moving..." : "Confirm the change"}</Button>
                <Button type="button" variant="ghost" disabled={busy} onClick={() => api.myBookings(stage.token).then((bookings) => setStage({ name: "list", token: stage.token, bookings })).catch(failed)}>Back</Button>
              </div>
            </div>
          )}
          {error && <p className="bk-error" role="alert">{error}</p>}
        </>
      )}

      {stage.name === "done" && (
        <div className="bk-card">
          <p className="bk-ok" style={{ margin: 0, fontWeight: 600 }}>{stage.text}</p>
          <p className="bk-muted" style={{ margin: "10px 0 0" }}>To change another booking we will check your mobile again.</p>
          <div className="bk-row" style={{ marginTop: 14 }}>
            <Button type="button" variant="primary" onClick={onBook}>Make a booking</Button>
            <Button type="button" variant="ghost" onClick={() => restart(null)}>Change another booking</Button>
          </div>
        </div>
      )}
    </section>
  );
}
